import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import classNames from 'classnames';

import { createOverlapCollisionDetection } from '../utils/collision-detection';

import { SortableItem } from './sortable-item';
import { SortableListProps } from './sortable-list.types';

const haveSameOrder = (left: UniqueIdentifier[], right: UniqueIdentifier[]) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

/**
 * SortableList - Generic drag and drop list component
 *
 * Simple, reliable implementation following the project's existing patterns.
 * No DragOverlay, just transform-based movement with opacity feedback.
 *
 * @example
 * ```tsx
 * <SortableList
 *   items={sections}
 *   onReorder={setSections}
 *   getItemId={(s) => s.id}
 *   renderItem={(section) => (
 *     <>
 *       <div className="flex-1">
 *         <p className="text-headline-xs text-primary">{section.title}</p>
 *       </div>
 *       <p className="text-body-xs text-secondary">{section.type}</p>
 *     </>
 *   )}
 * />
 * ```
 */
export function SortableList<T>(props: Readonly<SortableListProps<T>>) {
  const {
    items,
    onReorder,
    renderItem,
    getItemId,
    isItemDisabled,
    className,
    itemClassName,
    disabled = false,
    modifiers = [restrictToParentElement],
    collisionDetection: customCollisionDetection,
    dataTestId = 'sortable-list',
    itemsHandleOwnSortable = false,
    isOverlayActive = true,
  } = props;

  // Memoize collision detection to avoid recreating on every render
  const defaultCollisionDetection = useMemo(() => createOverlapCollisionDetection(), []);
  const collisionDetection = customCollisionDetection ?? defaultCollisionDetection;

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeItem, setActiveItem] = useState<T | null>(null);
  const [displayItems, setDisplayItems] = useState<T[]>(items);
  const [pendingOrderIds, setPendingOrderIds] = useState<UniqueIdentifier[] | null>(null);
  const latestIncomingOrderRef = useRef<UniqueIdentifier[]>(items.map((item) => getItemId(item)));

  useEffect(() => {
    const incomingIds = items.map((item) => getItemId(item));

    if (pendingOrderIds) {
      const orderWasConfirmed = haveSameOrder(incomingIds, pendingOrderIds);
      const serverOrderChanged = !haveSameOrder(incomingIds, latestIncomingOrderRef.current);

      if (orderWasConfirmed || serverOrderChanged) {
        setDisplayItems(items);
        setPendingOrderIds(null);
        latestIncomingOrderRef.current = incomingIds;
      }

      return;
    }

    setDisplayItems(items);
    latestIncomingOrderRef.current = incomingIds;
  }, [items, pendingOrderIds, getItemId]);

  useEffect(() => {
    if (activeId) {
      setActiveItem(displayItems.find((item) => getItemId(item) === activeId) ?? null);
    } else {
      setActiveItem(null);
    }
  }, [activeId, displayItems, getItemId]);

  // Configure sensors for drag interactions
  // Optimized for Android: increased distance and added delay for better touch handling
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 15, // Increased from 10 to 15 for better Android touch handling
        delay: 100, // Added 100ms delay to prevent accidental drags
        tolerance: 5, // Added tolerance for touch movement
      },
    }),
    useSensor(KeyboardSensor, {
      scrollBehavior: 'Cypress' in globalThis ? 'auto' : undefined,
    })
  );

  const itemIds = displayItems.map((item) => getItemId(item));

  const getIndex = (id: UniqueIdentifier) => itemIds.indexOf(id as string);

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    setActiveItem(null);

    if (over) {
      const activeIndex = getIndex(active.id);
      const overIndex = getIndex(over.id);

      if (activeIndex >= 0 && activeIndex !== overIndex) {
        const reorderedItems = arrayMove(displayItems, activeIndex, overIndex);
        const reorderedIds = reorderedItems.map((item) => getItemId(item));
        setDisplayItems(reorderedItems);
        setPendingOrderIds(reorderedIds);
        onReorder(reorderedItems, activeIndex, overIndex, activeItem ?? undefined);
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveItem(null);
  };

  return (
    <DndContext
      collisionDetection={collisionDetection}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={modifiers}>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div className={classNames('flex flex-col gap-4', className)} data-testid={dataTestId}>
          {displayItems.map((item) => {
            const id = getItemId(item);
            const itemDisabled = disabled || (isItemDisabled?.(item) ?? false);

            // If items handle their own sortable logic, render them directly —
            // they will call useSortable internally.
            if (itemsHandleOwnSortable) {
              return <Fragment key={id}>{renderItem(item)}</Fragment>;
            }

            // Otherwise, wrap in SortableItem for standard drag behavior
            return (
              <SortableItem key={id} id={id} className={itemClassName} disabled={itemDisabled}>
                {(dragHandle) => renderItem(item, dragHandle)}
              </SortableItem>
            );
          })}
        </div>
      </SortableContext>
      {isOverlayActive && <DragOverlay>{activeItem ? renderItem(activeItem, undefined, true) : null}</DragOverlay>}
    </DndContext>
  );
}
